import React, { useState } from 'react';
import './Itinerary.css';

const Itinerary = () => {
  const [activeDay, setActiveDay] = useState(1);

  const itinerary = [
    {
      day: 1,
      title: "Meetup to Silver City",
      distance: "300+ miles",
      highlights: [
        "5:00 AM meetup for rider briefing and gear check",
        "Ride through the top of Route 666 and into Silver City",
        "If time permits: Mogollon Ghost Town, the Catwalk in Glenwood, and Gila Cliff Dwellings",
        "Group dinner at 100+ year old restaurant and opera house",
        "Optional nightcap and entertainment at nextdoor bar",
        "Route briefing for Day 2"
      ],
      stops: [
        { name: "Meetup", time: "5:00 AM", description: "Meetup and briefing" },
        {
          name: "If time permits",
          time: "Optional",
          description: "Scenic side trips en route to Silver City",
          optional: true,
          places: [
            { name: "Mogollon Ghost Town", url: "https://www.newmexico.org/places-to-visit/ghost-towns/mogollon/" },
            { name: "The Catwalk, Glenwood", url: "https://www.newmexico.org/places-to-visit/regions/southwest/catwalk/" },
            { name: "Gila Cliff Dwellings", url: "https://www.nps.gov/gicl/index.htm" }
          ]
        },
        { name: "Silver City, NM", time: "5:00 PM", description: "Hotel check-in" },
        { name: "Group Dinner", time: "7:00 PM", description: "100+ year old restaurant and opera house" }
      ]
    },
    {
      day: 2,
      title: "The Devil's Highway - Main Event",
      distance: "200+ miles",
      highlights: [
        "Early departure for the legendary Devil's Highway",
        "Navigate 650+ curves over 120 miles",
        "Experience 5,000 feet of elevation change",
        "Scenic stops at overlooks and historic markers",
        "Technical riding through Apache-Sitgreaves National Forest"
      ],
      stops: [
        { name: "Breakfast", time: "6:00 AM", description: "Fantasitic local breakfast" },
        { name: "Silver City, NM", time: "8:00 AM", description: "Head to the mine!" },
        { name: "Morenci, AZ", time: "10:00 AM", description: "Start of Devil's Highway" },
        { name: "Hannagan Meadow", time: "2:00 PM", description: "Lunch and fuel stop" },
        { name: "Alpine", time: "4:00 PM", description: "End of Devil's Highway" },
        { name: "Lodge Check-in", time: "5:00 PM", description: "Mountain lodge accommodation" },
        { name: "Group Dinner", time: "7:00 PM", description: "Dinner at lodge restaurant" }
      ]
    },
    {
      day: 3,
      title: "Return Journey & Celebration",
      distance: "300+ miles",
      highlights: [
        "Scenic return route through Big Lake (and possible Salt River Canyon)",
        "Final group meal and celebration",
        "Souvenir patch presentation",
        "Return to Meetup with unforgettable memories"
      ],
      stops: [
        { name: "Meetup", time: "8:00 AM", description: "Briefing and group ride" },
        { name: "Big Lake", time: "11:00 AM", description: "Brunch" },
        { name: "Celebration", time: "1:00 PM", description: "Photo ops and farewell" },
        { name: "Meetup Return", time: "2:00 PM", description: "End of adventure" }
      ]
    }
  ];

  return (
    <section id="itinerary" className="itinerary section">
      <div className="container">
        <div className="section-header">
          <h2 className="section-title">3-Day Itinerary</h2>
          <p className="section-subtitle">
            Your complete journey through the Devil's Highway and beyond
          </p>
        </div>

        <div className="itinerary-tabs">
          {itinerary.map((day) => (
            <button
              key={day.day}
              className={`tab-button ${activeDay === day.day ? 'active' : ''}`}
              onClick={() => setActiveDay(day.day)}
            >
              <span className="tab-day">Day {day.day}</span>
              <span className="tab-title">{day.title}</span>
              <span className="tab-distance">{day.distance}</span>
            </button>
          ))}
        </div>

        <div className="itinerary-content">
          {itinerary.map((day) => (
            <div
              key={day.day}
              className={`day-content ${activeDay === day.day ? 'active' : ''}`}
            >
              <div className="day-header">
                <h3>Day {day.day}: {day.title}</h3>
                <div className="day-stats">
                  <span className="stat">
                    <span className="stat-icon">📏</span>
                    {day.distance}
                  </span>
                  <span className="stat">
                    <span className="stat-icon">⏱️</span>
                    Full Day
                  </span>
                  <span className="stat">
                    <span className="stat-icon">🏍️</span>
                    Advanced
                  </span>
                </div>
              </div>

              <div className="day-grid">
                <div className="highlights-section">
                  <h4>Day Highlights</h4>
                  <ul className="highlights-list">
                    {day.highlights.map((highlight, index) => (
                      <li key={index} className="highlight-item">
                        <span className="highlight-icon">✨</span>
                        {highlight}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="stops-section">
                  <h4>Schedule & Stops</h4>
                  <div className="stops-timeline">
                    {day.stops.map((stop, index) => (
                      <div key={index} className="stop-item">
                        <div className={`stop-time${stop.optional ? ' optional' : ''}`}>{stop.time}</div>
                        <div className="stop-details">
                          <h5 className="stop-name">{stop.name}</h5>
                          <p className="stop-description">{stop.description}</p>
                          {stop.places && (
                            <ul className="optional-places">
                              {stop.places.map((place) => (
                                <li key={place.name}>
                                  <a href={place.url} className="stop-link" target="_blank" rel="noopener noreferrer">
                                    {place.name}
                                  </a>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                        {index < day.stops.length - 1 && <div className="timeline-connector"></div>}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="itinerary-note">
          <div className="note-content">
            <span className="note-icon">📝</span>
            <div>
              <h4>Important Notes</h4>
              <p>
                All times are approximate and may vary based on weather, group pace, and road conditions. 
                Our professional guide will adjust the schedule as needed to ensure safety and maximize 
                your experience. Meals and accommodations are included as specified in your package.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Itinerary;
